import { useCallback, useEffect, useState } from 'react';
import { useT } from '../../i18n';
import { api } from '../../api/client';
import { useAuth } from '../../context/AuthContext';
import { useRole } from '../../context/RoleContext';
import { TaskTimeline } from '../../components/TaskTimeline';
import type { TaskUpdate } from '../../types';
import { todoApi } from './api';

// [Proposition: vue-jalons-taches] A to-do item's own comment thread — the
// same thread as a task's (replies, @-mentions that notify in the app and by
// e-mail), without file attachments (documents go on the item's paperclip).
// Editing and deleting a comment go through the task's comment routes.
export function TodoComments({ todoId, taskId, onCount }: { todoId: number; taskId: number; onCount?: (n: number) => void }) {
  const { t } = useT();
  const { user } = useAuth();
  const { isAdmin } = useRole();
  const [comments, setComments] = useState<TaskUpdate[] | null>(null);

  const load = useCallback(() => todoApi.comments(todoId).then(c => { setComments(c); onCount?.(c.length); }).catch(() => setComments([])), [todoId]);
  useEffect(() => { load(); }, [load]);

  if (!user || comments == null) return null;
  return (
    <div className="vjt-todo-comments">
      <div className="docs-hint">{t('vjt.todo.commentsHint')}</div>
      <TaskTimeline
        updates={comments}
        onPost={async (content, type, replyToId, taggedUserIds) => {
          const c = await todoApi.addComment(todoId, content, { update_type: type, reply_to_id: replyToId, tagged_user_ids: taggedUserIds });
          await load();
          return c.id;
        }}
        onEdit={async (id, content) => { await api.editTaskUpdate(taskId, id, content); await load(); }}
        onDelete={async (id) => { await api.deleteTaskUpdate(taskId, id); await load(); }}
        currentUserId={user.id}
        isAdmin={isAdmin}
        taskId={taskId}
        hideHeader
        allowAttachments={false}
        onRefresh={load}
      />
    </div>
  );
}
